<template>
  <b-modal
    ref="modal"
    title="Novo Denunciado"
    ok-title="Salvar"
    cancel-title="Cancelar"
    size="lg"
    @ok="onSaveClicked($event)"
  >
    <b-form>
      <!-- Campo de Pesquisa -->
      <div class="search-section mb-4">
        <h6 class="section-title">Buscar Relacionamento Existente</h6>
        <b-row>
          <b-col cols="12" md="8">
            <b-form-group label="Pesquisar por nome, CPF/CNPJ ou email">
              <b-form-input
                v-model="searchTerm"
                placeholder="Digite o nome, CPF/CNPJ ou email para buscar..."
                :disabled="searching"
              ></b-form-input>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="4">
            <b-button
              variant="primary"
              :disabled="!searchTerm || searching"
              class="mt-4"
              block
              @click="performSearch"
            >
              <b-spinner v-if="searching" small></b-spinner>
              <i v-else class="fas fa-search"></i>
              {{ searching ? "Buscando..." : "Buscar" }}
            </b-button>
          </b-col>
        </b-row>
      </div>

      <!-- Resultados da Pesquisa -->
      <div v-if="searchResults.length > 0" class="search-results mb-4">
        <h6 class="section-title">Resultados Encontrados</h6>
        <div class="results-list">
          <div
            v-for="result in searchResults"
            :key="result._id"
            class="result-item p-3 mb-2 border rounded"
            :class="{
              'selected-result':
                selectedRelationship && selectedRelationship._id === result._id,
            }"
          >
            <div class="d-flex justify-content-between align-items-start">
              <div class="result-info">
                <strong>{{ getRelationshipName(result) }}</strong>
                <div
                  v-if="result.investigationsCount > 0"
                  class="investigation-warning small mt-1"
                >
                  <i class="fas fa-exclamation-triangle"></i>
                  Já investigado {{ result.investigationsCount }}
                  {{ result.investigationsCount > 1 ? "vezes" : "vez" }}
                </div>
                <div class="text-muted small">
                  <span v-if="result.relationshipType === 'internal'">
                    Funcionário Interno
                  </span>
                  <span v-else>
                    Terceiro -
                    {{ result.type === "individuo" ? "Indivíduo" : "Entidade" }}
                  </span>
                  <span v-if="result.cpfcnpj">
                    • CPF/CNPJ: {{ result.cpfcnpj }}</span
                  >
                </div>
                <div
                  v-if="getRelationshipEmail(result)"
                  class="text-muted small"
                >
                  Email: {{ getRelationshipEmail(result) }}
                </div>
                <div
                  v-if="
                    result.relationshipType === 'internal' &&
                    (result.position || result.area)
                  "
                  class="text-muted small"
                >
                  <span v-if="result.position"
                    >Cargo: {{ result.position }}</span
                  >
                  <span v-if="result.position && result.area"> • </span>
                  <span v-if="result.area">Setor: {{ result.area }}</span>
                </div>
              </div>
              <b-button
                size="sm"
                :variant="
                  selectedRelationship &&
                  selectedRelationship._id === result._id
                    ? 'success'
                    : 'outline-primary'
                "
                @click="selectRelationship(result)"
              >
                {{
                  selectedRelationship &&
                  selectedRelationship._id === result._id
                    ? "Selecionado"
                    : "Selecionar"
                }}
              </b-button>
            </div>
          </div>
        </div>
      </div>

      <!-- Nenhum resultado encontrado -->
      <div
        v-if="searchPerformed && searchResults.length === 0 && !searching"
        class="no-results mb-4"
      >
        <div class="alert alert-warning">
          <i class="fas fa-exclamation-triangle"></i>
          <strong>Nenhum relacionamento encontrado</strong> para "<strong>{{
            searchTerm
          }}</strong
          >".
        </div>
        <div class="registration-options">
          <p class="mb-3">Deseja cadastrar uma nova pessoa/empresa?</p>
          <div class="d-flex gap-2">
            <b-button variant="primary" @click="showInternalForm">
              <i class="fas fa-user-plus"></i>
              Cadastrar Funcionário Interno
            </b-button>
            <b-button variant="info" @click="showThirdPartyForm">
              <i class="fas fa-handshake"></i>
              Cadastrar Terceiro
            </b-button>
          </div>
        </div>
      </div>

      <!-- Resultados encontrados -->
      <div v-if="searchResults.length > 0" class="search-results mb-4">
        <div class="alert alert-success">
          <i class="fas fa-check-circle"></i>
          <strong
            >{{ searchResults.length }} relacionamento(s) encontrado(s)</strong
          >
          para "<strong>{{ searchTerm }}</strong
          >".
        </div>
      </div>

      <!-- Formulário de Funcionário Interno -->
      <div v-if="showForm === 'internal'" class="form-section mb-4">
        <h6 class="section-title">Cadastrar Funcionário Interno</h6>
        <b-row>
          <b-col cols="12" md="6">
            <b-form-group label="Nome">
              <b-form-input
                v-model="newInternal.name"
                required
                placeholder="Nome completo"
              ></b-form-input>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label="Email">
              <b-form-input
                v-model="newInternal.email"
                type="email"
                placeholder="Email (opcional)"
              ></b-form-input>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label="CPF/CNPJ">
              <b-form-input
                v-model="newInternal.cpfcnpj"
                placeholder="CPF ou CNPJ (opcional)"
              ></b-form-input>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label="Cargo">
              <b-form-input
                v-model="newInternal.position"
                placeholder="Cargo (opcional)"
              ></b-form-input>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label="Área">
              <b-form-select
                v-model="newInternal.area"
                :options="internalAreaOptions"
                :disabled="loadingAreas"
              ></b-form-select>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label="Telefone">
              <b-form-input
                v-model="newInternal.phone"
                placeholder="Telefone (opcional)"
              ></b-form-input>
            </b-form-group>
          </b-col>
        </b-row>
        <div class="form-actions mt-3">
          <b-button
            variant="success"
            :disabled="creatingInternal"
            @click="createInternalEmployee"
          >
            <b-spinner v-if="creatingInternal" small></b-spinner>
            <i v-else class="fas fa-save"></i>
            {{ creatingInternal ? "Criando..." : "Criar e Continuar" }}
          </b-button>
          <b-button variant="secondary" class="ml-2" @click="cancelForm">
            Cancelar
          </b-button>
        </div>
      </div>

      <!-- Formulário de Terceiro -->
      <div v-if="showForm === 'third'" class="form-section mb-4">
        <h6 class="section-title">Cadastrar Terceiro</h6>
        <b-row>
          <b-col cols="12" md="6">
            <b-form-group label="Tipo">
              <b-form-select
                v-model="newThird.type"
                :options="thirdTypeOptions"
                required
              ></b-form-select>
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Campos para Indivíduo -->
        <div v-if="newThird.type === 'individuo'">
          <b-row>
            <b-col cols="12" md="6">
              <b-form-group label="Nome">
                <b-form-input
                  v-model="newThird.name"
                  required
                  placeholder="Nome completo"
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
              <b-form-group label="Email">
                <b-form-input
                  v-model="newThird.personEmail"
                  type="email"
                  placeholder="Email (opcional)"
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
              <b-form-group label="CPF/CNPJ">
                <b-form-input
                  v-model="newThird.cpfcnpj"
                  placeholder="CPF ou CNPJ (opcional)"
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
              <b-form-group label="Telefone">
                <b-form-input
                  v-model="newThird.personPhone"
                  placeholder="Telefone (opcional)"
                ></b-form-input>
              </b-form-group>
            </b-col>
          </b-row>
        </div>

        <!-- Campos para Entidade -->
        <div v-if="newThird.type === 'entidade'">
          <b-row>
            <b-col cols="12" md="6">
              <b-form-group label="Razão Social">
                <b-form-input
                  v-model="newThird.name"
                  required
                  placeholder="Razão social"
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
              <b-form-group label="CNPJ">
                <b-form-input
                  v-model="newThird.cpfcnpj"
                  placeholder="CNPJ (opcional)"
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
              <b-form-group label="Email">
                <b-form-input
                  v-model="newThird.entityEmail"
                  type="email"
                  placeholder="Email da entidade (opcional)"
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
              <b-form-group label="Telefone">
                <b-form-input
                  v-model="newThird.entityPhone"
                  placeholder="Telefone (opcional)"
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
              <b-form-group label="Nome do Responsável">
                <b-form-input
                  v-model="newThird.responsibleName"
                  placeholder="Nome do responsável (opcional)"
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
              <b-form-group label="Email do Responsável">
                <b-form-input
                  v-model="newThird.responsibleEmail"
                  type="email"
                  placeholder="Email do responsável (opcional)"
                ></b-form-input>
              </b-form-group>
            </b-col>
          </b-row>
        </div>

        <div class="form-actions mt-3">
          <b-button
            variant="success"
            :disabled="creatingThird"
            @click="createThirdParty"
          >
            <b-spinner v-if="creatingThird" small></b-spinner>
            <i v-else class="fas fa-save"></i>
            {{ creatingThird ? "Criando..." : "Criar e Continuar" }}
          </b-button>
          <b-button variant="secondary" class="ml-2" @click="cancelForm">
            Cancelar
          </b-button>
        </div>
      </div>

      <!-- Exibir dados do relacionamento selecionado -->
      <div
        v-if="selectedRelationship && !showForm"
        class="selected-relationship-info mb-4"
      >
        <div class="info-card">
          <h6 class="info-title">
            <i class="fas fa-user-check text-success"></i>
            Dados do Relacionamento Selecionado
          </h6>
          <div class="info-content">
            <div class="info-row">
              <strong>Nome:</strong>
              <span>{{ getRelationshipName(selectedRelationship) }}</span>
            </div>
            <div v-if="selectedRelationship.cpfcnpj" class="info-row">
              <strong>CPF/CNPJ:</strong>
              <span>{{ selectedRelationship.cpfcnpj }}</span>
            </div>
            <div
              v-if="
                selectedRelationship.relationshipType === 'internal' &&
                selectedRelationship.position
              "
              class="info-row"
            >
              <strong>Cargo:</strong>
              <span>{{ selectedRelationship.position }}</span>
            </div>
            <div
              v-if="
                selectedRelationship.relationshipType === 'internal' &&
                selectedRelationship.area
              "
              class="info-row"
            >
              <strong>Área/Setor:</strong>
              <span>{{ selectedRelationship.area }}</span>
            </div>
            <div
              v-if="getRelationshipEmail(selectedRelationship)"
              class="info-row"
            >
              <strong>Email:</strong>
              <span>{{ getRelationshipEmail(selectedRelationship) }}</span>
            </div>
            <div v-if="selectedRelationship.phone" class="info-row">
              <strong>Telefone:</strong>
              <span>{{ selectedRelationship.phone }}</span>
            </div>
            <div class="info-row">
              <strong>Tipo:</strong>
              <span>
                <b-badge
                  :variant="
                    selectedRelationship.relationshipType === 'internal'
                      ? 'primary'
                      : 'info'
                  "
                >
                  {{
                    selectedRelationship.relationshipType === "internal"
                      ? "Funcionário Interno"
                      : "Terceiro"
                  }}
                </b-badge>
              </span>
            </div>
          </div>
        </div>
      </div>
    </b-form>
  </b-modal>
</template>

<script>
import { addDenounced } from "@/api/complaint";
import { searchRelationships, createRelationship } from "@/api/relationships";
import { getAreas } from "@/api/nr1";

export default {
  name: "DenouncedModal",
  props: {
    complaint: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      denounced: {},
      cpfcnpjError: "",
      cpfcnpjState: null,
      rules: {
        name: { required: true },
        office: { required: true },
        department: { required: true },
      },
      // Pesquisa
      searchTerm: "",
      searching: false,
      searchResults: [],
      searchPerformed: false,
      selectedRelationship: null,
      // Formulários
      showForm: null, // 'internal', 'third', or null
      newInternal: {
        name: "",
        email: "",
        phone: "",
        area: "",
        position: "",
        cpfcnpj: "",
        active: true,
      },
      newThird: {
        type: "individuo",
        name: "",
        personEmail: "",
        personPhone: "",
        entityEmail: "",
        entityPhone: "",
        responsibleName: "",
        responsibleEmail: "",
        cpfcnpj: "",
        active: true,
      },
      creatingInternal: false,
      creatingThird: false,
      loadingAreas: false,
      internalAreaOptions: [
        {
          value: "",
          text: "Não informado (opcional)",
        },
      ],
      thirdTypeOptions: [
        { value: "individuo", text: "Indivíduo" },
        { value: "entidade", text: "Entidade" },
      ],
    };
  },
  methods: {
    getComplaintCompanyId() {
      if (!this.complaint || !this.complaint.company) {
        return null;
      }

      const rawCompanyId =
        this.complaint.company._id || this.complaint.company || null;
      const companyId = parseInt(rawCompanyId, 10);

      if (Number.isNaN(companyId)) {
        return null;
      }

      return companyId;
    },

    validateState(ref) {
      if (
        this.vFields[ref] &&
        (this.vFields[ref].dirty || this.vFields[ref].validated)
      ) {
        return !this.errors.has(ref);
      }
      return null;
    },

    validateCpfcnpj(value) {
      // Check if field is empty (required validation)
      if (!value || value.trim() === "") {
        this.cpfcnpjError = "O campo CPF ou CNPJ é obrigatório";
        this.cpfcnpjState = false;
        return false;
      }

      // Remove any non-digit characters
      const cleanValue = value.replace(/\D/g, "");

      // Check if we have any digits after cleaning
      if (cleanValue.length === 0) {
        this.cpfcnpjError = "Digite apenas números";
        this.cpfcnpjState = false;
        return false;
      }

      // If it's 11 digits, validate as CPF
      if (cleanValue.length === 11) {
        if (!this.validateCPF(cleanValue)) {
          this.cpfcnpjError = "CPF inválido";
          this.cpfcnpjState = false;
          return false;
        }
        this.cpfcnpjError = "";
        this.cpfcnpjState = true;
        return true;
      }
      // If it's 14 digits, validate as CNPJ
      else if (cleanValue.length === 14) {
        if (!this.validateCNPJ(cleanValue)) {
          this.cpfcnpjError = "CNPJ inválido";
          this.cpfcnpjState = false;
          return false;
        }
        this.cpfcnpjError = "";
        this.cpfcnpjState = true;
        return true;
      }
      // Otherwise, treat as internal ID (must have at least 1 character)
      else {
        this.cpfcnpjError = "";
        this.cpfcnpjState = true;
        return true;
      }
    },

    validateCPF(cpf) {
      if (cpf === "") return false;

      if (
        cpf.length !== 11 ||
        cpf === "00000000000" ||
        cpf === "11111111111" ||
        cpf === "22222222222" ||
        cpf === "33333333333" ||
        cpf === "44444444444" ||
        cpf === "55555555555" ||
        cpf === "66666666666" ||
        cpf === "77777777777" ||
        cpf === "88888888888" ||
        cpf === "99999999999"
      ) {
        return false;
      }

      let add = 0;
      for (let i = 0; i < 9; i++) {
        add += parseInt(cpf.charAt(i)) * (10 - i);
      }
      let rev = 11 - (add % 11);
      if (rev === 10 || rev === 11) {
        rev = 0;
      }

      if (rev !== parseInt(cpf.charAt(9))) return false;

      add = 0;
      for (let i = 0; i < 10; i++) {
        add += parseInt(cpf.charAt(i)) * (11 - i);
      }
      rev = 11 - (add % 11);
      if (rev === 10 || rev === 11) {
        rev = 0;
      }
      return !(rev !== parseInt(cpf.charAt(10)));
    },

    validateCNPJ(cnpj) {
      if (cnpj === "") return false;
      if (cnpj.length !== 14) return false;

      if (
        cnpj === "00000000000000" ||
        cnpj === "11111111111111" ||
        cnpj === "22222222222222" ||
        cnpj === "33333333333333" ||
        cnpj === "44444444444444" ||
        cnpj === "55555555555555" ||
        cnpj === "66666666666666" ||
        cnpj === "77777777777777" ||
        cnpj === "88888888888888" ||
        cnpj === "99999999999999"
      ) {
        return false;
      }

      let length = cnpj.length - 2;
      let numbers = cnpj.substring(0, length);
      const digits = cnpj.substring(length);
      let sum = 0;
      let pos = length - 7;
      for (let i = length; i >= 1; i--) {
        sum += parseInt(numbers.charAt(length - i)) * pos--;
        if (pos < 2) {
          pos = 9;
        }
      }

      let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
      if (result !== parseInt(digits.charAt(0))) return false;

      length = length + 1;
      numbers = cnpj.substring(0, length);
      sum = 0;
      pos = length - 7;
      for (let i = length; i >= 1; i--) {
        sum += parseInt(numbers.charAt(length - i)) * pos--;
        if (pos < 2) pos = 9;
      }
      result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
      return !(result !== parseInt(digits.charAt(1)));
    },

    onCpfcnpjInput() {
      // Remove any non-digit characters from input
      if (this.denounced.cpfcnpj) {
        this.denounced.cpfcnpj = this.denounced.cpfcnpj.replace(/\D/g, "");
      }
      // Validate the input
      this.validateCpfcnpj(this.denounced.cpfcnpj);
    },

    // Métodos de pesquisa
    async performSearch() {
      if (!this.searchTerm || this.searchTerm.trim().length < 2) {
        this.$bvToast.toast("Digite pelo menos 2 caracteres para pesquisar.", {
          title: "Atenção",
          toastClass: "custom-toast custom-toast-warning",
          variant: "light",
          solid: true,
          autoHideDelay: 8000,
          noCloseButton: false,
        });
        return;
      }

      this.searching = true;
      try {
        const searchValue = this.searchTerm.trim();
        const complaintCompanyId = this.getComplaintCompanyId();
        const response = await searchRelationships(
          searchValue,
          complaintCompanyId
        );

        this.searchResults = (response.data || []).map((item) => ({
          ...item,
          investigationsCount: Number(item.investigationsCount) || 0,
        }));
        this.searchPerformed = true;

        if (this.searchResults.length === 0) {
          this.$bvToast.toast(
            `Nenhum relacionamento encontrado para "${searchValue}".`,
            {
              title: "Informação",
              toastClass: "custom-toast custom-toast-info",
              variant: "light",
              solid: true,
              autoHideDelay: 5000,
              noCloseButton: false,
            }
          );
        }
      } catch (error) {
        this.$bvToast.toast(
          "Erro ao realizar pesquisa: " +
            (error.message || "Erro desconhecido"),
          {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          }
        );
        this.searchResults = [];
      } finally {
        this.searching = false;
      }
    },

    getRelationshipName(relationship) {
      if (relationship.relationshipType === "internal") {
        return relationship.name;
      } else {
        if (relationship.type === "individuo") {
          return relationship.personName || relationship.name;
        } else {
          return relationship.entityName || relationship.name;
        }
      }
    },

    getRelationshipEmail(relationship) {
      if (relationship.relationshipType === "internal") {
        return relationship.email;
      } else {
        if (relationship.type === "individuo") {
          return relationship.personEmail;
        } else {
          return relationship.entityEmail;
        }
      }
    },

    selectRelationship(relationship) {
      this.selectedRelationship = relationship;
      this.fillDenouncedFromRelationship(relationship);
    },

    fillDenouncedFromRelationship(relationship) {
      this.denounced = {
        name: this.getRelationshipName(relationship),
        cpfcnpj: relationship.cpfcnpj || `REL_${relationship._id}`, // Usar ID do relacionamento se não houver CPF/CNPJ
        office:
          relationship.relationshipType === "internal"
            ? relationship.position || ""
            : "",
        department:
          relationship.relationshipType === "internal"
            ? relationship.area || ""
            : "",
      };
    },

    // Métodos de formulário
    async showInternalForm() {
      this.showForm = "internal";
      this.resetNewInternal();
      await this.loadInternalAreaOptions();
    },

    showThirdPartyForm() {
      this.showForm = "third";
      this.resetNewThird();
    },

    cancelForm() {
      this.showForm = null;
      this.selectedRelationship = null;
      this.resetDenounced();
    },

    resetDenounced() {
      this.denounced = {};
      this.cpfcnpjError = "";
      this.cpfcnpjState = null;
    },

    resetNewInternal() {
      this.newInternal = {
        name: "",
        email: "",
        phone: "",
        area: "",
        position: "",
        cpfcnpj: "",
        active: true,
      };
    },

    resetNewThird() {
      this.newThird = {
        type: "individuo",
        name: "",
        personEmail: "",
        personPhone: "",
        entityEmail: "",
        entityPhone: "",
        responsibleName: "",
        responsibleEmail: "",
        cpfcnpj: "",
        active: true,
      };
    },

    async loadInternalAreaOptions() {
      const complaintCompanyId = this.getComplaintCompanyId();

      this.internalAreaOptions = [
        {
          value: "",
          text: "Não informado (opcional)",
        },
      ];

      if (!complaintCompanyId) {
        return;
      }

      this.loadingAreas = true;
      try {
        const response = await getAreas({ company: complaintCompanyId });
        const options = (response.data || [])
          .filter((area) => !!area)
          .map((area) => ({
            value: area,
            text: area,
          }));

        this.internalAreaOptions = [...this.internalAreaOptions, ...options];
      } catch (error) {
        this.$bvToast.toast(
          "Não foi possível carregar os setores da empresa da denúncia.",
          {
            title: "Atenção",
            toastClass: "custom-toast custom-toast-warning",
            variant: "light",
            solid: true,
            autoHideDelay: 8000,
            noCloseButton: false,
          }
        );
      } finally {
        this.loadingAreas = false;
      }
    },

    async createInternalEmployee() {
      if (!this.newInternal.name) {
        this.$bvToast.toast("Nome é obrigatório.", {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 10000,
          noCloseButton: false,
        });
        return;
      }

      if (!this.newInternal.name.trim().includes(" ")) {
        this.$bvToast.toast("Informe o nome completo (Nome e Sobrenome).", {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 10000,
          noCloseButton: false,
        });
        return;
      }

      this.creatingInternal = true;
      try {
        const complaintCompanyId = this.getComplaintCompanyId();
        if (!complaintCompanyId) {
          this.$bvToast.toast(
            "Não foi possível identificar a empresa da denúncia.",
            {
              title: "Erro",
              toastClass: "custom-toast custom-toast-error",
              variant: "light",
              solid: true,
              autoHideDelay: 10000,
              noCloseButton: false,
            }
          );
          return;
        }

        const relationshipData = {
          ...this.newInternal,
          relationshipType: "internal",
          company: complaintCompanyId,
        };

        const response = await createRelationship(relationshipData);

        this.$bvToast.toast("Funcionário interno criado com sucesso!", {
          title: "Sucesso",
          toastClass: "custom-toast custom-toast-success",
          variant: "light",
          solid: true,
          autoHideDelay: 5000,
          noCloseButton: false,
        });

        // Preencher o formulário principal com os dados criados
        this.selectedRelationship = response.data;
        this.fillDenouncedFromRelationship(response.data);
        this.showForm = null;
      } catch (error) {
        const errorMessage =
          error?.response?.data?.message ||
          "Erro ao criar funcionário interno.";

        this.$bvToast.toast(errorMessage, {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 10000,
          noCloseButton: false,
        });
      } finally {
        this.creatingInternal = false;
      }
    },

    async createThirdParty() {
      if (!this.newThird.name) {
        this.$bvToast.toast("Nome é obrigatório.", {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 10000,
          noCloseButton: false,
        });
        return;
      }

      this.creatingThird = true;
      try {
        const complaintCompanyId = this.getComplaintCompanyId();
        if (!complaintCompanyId) {
          this.$bvToast.toast(
            "Não foi possível identificar a empresa da denúncia.",
            {
              title: "Erro",
              toastClass: "custom-toast custom-toast-error",
              variant: "light",
              solid: true,
              autoHideDelay: 10000,
              noCloseButton: false,
            }
          );
          return;
        }

        const relationshipData = {
          ...this.newThird,
          relationshipType: "third-party",
          company: complaintCompanyId,
        };

        const response = await createRelationship(relationshipData);

        this.$bvToast.toast("Terceiro criado com sucesso!", {
          title: "Sucesso",
          toastClass: "custom-toast custom-toast-success",
          variant: "light",
          solid: true,
          autoHideDelay: 5000,
          noCloseButton: false,
        });

        // Preencher o formulário principal com os dados criados
        this.selectedRelationship = response.data;
        this.fillDenouncedFromRelationship(response.data);
        this.showForm = null;
      } catch (error) {
        this.$bvToast.toast("Erro ao criar terceiro.", {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 10000,
          noCloseButton: false,
        });
      } finally {
        this.creatingThird = false;
      }
    },

    async onSaveClicked(bvEvt) {
      bvEvt.preventDefault();

      // Se há relacionamento selecionado, não precisa validar campos
      if (this.selectedRelationship) {
        try {
          await addDenounced(this.complaint._id, this.denounced);
        } catch (error) {
          if (error.response.status === 403 || error.response.status === 404) {
            this.$bvToast.toast(error.response.data, {
              title: "Erro",
              toastClass: "custom-toast custom-toast-error",
              variant: "light",
              solid: true,
              autoHideDelay: 10000,
              noCloseButton: false,
            });
          } else {
            this.$bvToast.toast(error.response.statusText, {
              title: "Erro",
              toastClass: "custom-toast custom-toast-error",
              variant: "light",
              solid: true,
              autoHideDelay: 10000,
              noCloseButton: false,
            });
          }
          return;
        }

        this.$bvToast.toast("Denunciado adicionado com sucesso!", {
          title: "Sucesso",
          toastClass: "custom-toast custom-toast-success",
          variant: "light",
          solid: true,
          autoHideDelay: 5000,
          noCloseButton: false,
        });
        this.$emit("new-denounced");

        this.$refs.modal.hide();
        return;
      }

      // Se não há relacionamento selecionado, mostrar erro
      this.$bvToast.toast(
        "Por favor, selecione um relacionamento ou cadastre um novo.",
        {
          title: "Atenção",
          toastClass: "custom-toast custom-toast-warning",
          variant: "light",
          solid: true,
          autoHideDelay: 8000,
          noCloseButton: false,
        }
      );
    },

    show() {
      this.$refs.modal.show();
      // Reset validation state when modal opens
      this.cpfcnpjError = "";
      this.cpfcnpjState = null;
      this.resetAll();
    },

    resetAll() {
      this.searchTerm = "";
      this.searching = false;
      this.searchResults = [];
      this.searchPerformed = false;
      this.selectedRelationship = null;
      this.showForm = null;
      this.loadingAreas = false;
      this.internalAreaOptions = [
        {
          value: "",
          text: "Não informado (opcional)",
        },
      ];
      this.resetDenounced();
      this.resetNewInternal();
      this.resetNewThird();
    },
  },
};
</script>

<style scoped>
.search-section {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
}

.section-title {
  color: #495057;
  font-weight: 600;
  margin-bottom: 1rem;
  font-size: 1rem;
}

.search-results {
  max-height: 300px;
  overflow-y: auto;
}

.result-item {
  transition: background-color 0.2s ease;
  cursor: pointer;
}

.result-item:hover {
  background-color: #f8f9fa !important;
}

.investigation-warning {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  color: #856404;
  background-color: #fff3cd;
  border: 1px solid #ffeeba;
  border-radius: 4px;
  padding: 0.2rem 0.45rem;
  font-weight: 600;
}

.selected-result {
  background-color: #d4edda !important;
  border-color: #c3e6cb !important;
}

.no-results .alert {
  margin-bottom: 1rem;
}

.registration-options .btn {
  margin-right: 0.5rem;
}

.form-section {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  border: 1px solid #e9ecef;
}

.form-actions {
  padding-top: 1rem;
  border-top: 1px solid #e9ecef;
}

.selected-relationship-info {
  background: white;
  border-radius: 8px;
  border: 2px solid #28a745;
  overflow: hidden;
}

.info-card {
  padding: 1rem;
}

.info-title {
  color: #28a745;
  font-weight: 600;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e9ecef;
}

.info-content {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f8f9fa;
}

.info-row:last-child {
  border-bottom: none;
}

.info-row strong {
  color: #495057;
  font-weight: 600;
  min-width: 120px;
}

.info-row span {
  color: #6c757d;
  text-align: right;
  flex: 1;
}

/* Responsividade */
@media (max-width: 768px) {
  .registration-options .d-flex {
    flex-direction: column;
  }

  .registration-options .btn {
    margin-bottom: 0.5rem;
    margin-right: 0;
  }

  .search-section .d-flex {
    flex-direction: column;
  }

  .search-section .col-md-4 {
    margin-top: 1rem;
  }

  .info-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }

  .info-row strong {
    min-width: auto;
  }

  .info-row span {
    text-align: left;
  }
}

/* Melhorar aparência dos campos de formulário */
.form-group label {
  font-weight: 500;
  color: #495057;
  margin-bottom: 0.5rem;
}

.form-control,
.form-select {
  border-radius: 6px;
  border: 1px solid #ced4da;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.form-control:focus,
.form-select:focus {
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

/* Estilização do checkbox switch */
.custom-switch .custom-control-label::before {
  border-radius: 0.75rem;
}

.custom-switch .custom-control-label::after {
  border-radius: 0.75rem;
}
</style>
